import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

import { fetchSubscriptionDetail } from '../api/subscription';
import {
  redirectToLoginForSessionExpiration,
  registerSessionExpirationLogout,
  shouldHandleSessionExpiration,
} from '../utils/sessionExpired';
import { normalizeEmail } from '../utils/normalizeEmail';

// ベースURLの設定
const API_URL = process.env.REACT_APP_API_URL;
const COOKIE_SESSION_TOKEN = 'cookie-session';
const HEARTBEAT_INTERVAL_MS = 30 * 1000;
export const HEARTBEAT_TRANSIENT_FAILURE_WARNING_THRESHOLD = 3;
export const isHeartbeatAuthenticationFailure = (status?: number): boolean =>
  status === 401 || status === 403;

export const shouldShowHeartbeatConnectionWarning = (
  transientFailureCount: number,
): boolean => transientFailureCount >= HEARTBEAT_TRANSIENT_FAILURE_WARNING_THRESHOLD;

// ユーザー型定義
export interface User {
  id: string;
  username: string;
  email: string;
  hospitalName?: string;
  isAdmin: boolean;
  isVip: boolean;
  isTrial: boolean;
  isViewer: boolean;
  isExpired: boolean;
  stripeCustomerId?: string;
  hasActiveSubscription: boolean;
  hasPriorSubscriptionContract: boolean;
  disabled: boolean;
  justRegistered?: boolean;
  mustVerifyCode?: boolean;
  // Set only by this tab's login response, never reconstructed from /me.
  verificationChallengeId?: string;
  mustChangePassword?: boolean;
  needsProfileCompletion?: boolean;
  pendingEmail?: string;
  emailVerifiedAt?: string | null;
  isDefaultAdmin?: boolean;
  subscriptionPeriodEnd?: number | null;
  subscriptionCancelAtPeriodEnd?: boolean | null;
  subscriptionDetailFetchedAt?: number | null;
  trialExpiresAt?: string | null;
  previousLoginAt?: string | null;
}
export const hasFullAuthenticatedSession = (
  user: Pick<User, 'mustVerifyCode'> | null | undefined,
): boolean => Boolean(user && !user.mustVerifyCode);

export const shouldRefreshSubscriptionForUser = (
  user: Pick<User, 'mustVerifyCode' | 'isViewer'> | null | undefined,
): boolean => hasFullAuthenticatedSession(user) && !user?.isViewer;

// User型の正規化（安全補完）
function normalizeUser(raw: any): User {
  return {
    id: raw.id ?? "",
    username: raw.username ?? "",
    email: raw.email ?? "",
    hospitalName: raw.hospitalName ?? "",
    isAdmin: !!raw.isAdmin,
    isVip: !!raw.isVip,
    isTrial: !!(raw.isTrial ?? raw.is_trial),
    isViewer: !!raw.isViewer,
    isExpired: !!(
      raw.isExpired ??
      raw.is_expired ??
      raw.isContractInactive ??
      raw.is_contract_inactive
    ),
    stripeCustomerId: raw.stripeCustomerId ?? "",
    hasActiveSubscription: !!raw.hasActiveSubscription,
    hasPriorSubscriptionContract: !!(
      raw.hasPriorSubscriptionContract ?? raw.has_prior_subscription_contract
    ),
    disabled: !!raw.disabled,
    justRegistered: raw.justRegistered,
    mustVerifyCode: raw.mustVerifyCode,
    mustChangePassword: !!raw.mustChangePassword,
    needsProfileCompletion: !!raw.needsProfileCompletion,
    pendingEmail: raw.pendingEmail ?? undefined,
    emailVerifiedAt: raw.emailVerifiedAt ?? raw.email_verified_at ?? null,
    isDefaultAdmin: raw.isDefaultAdmin ?? false,
    subscriptionPeriodEnd:
      typeof raw.subscriptionPeriodEnd === 'number'
        ? raw.subscriptionPeriodEnd
        : typeof raw.subscription_period_end === 'number'
          ? raw.subscription_period_end
          : null,
    subscriptionCancelAtPeriodEnd:
      typeof raw.subscriptionCancelAtPeriodEnd === 'boolean'
        ? raw.subscriptionCancelAtPeriodEnd
        : typeof raw.subscription_cancel_at_period_end === 'boolean'
          ? raw.subscription_cancel_at_period_end
          : null,
    subscriptionDetailFetchedAt:
      typeof raw.subscriptionDetailFetchedAt === 'number'
        ? raw.subscriptionDetailFetchedAt
        : typeof raw.subscription_detail_fetched_at === 'number'
          ? raw.subscription_detail_fetched_at
          : null,
    trialExpiresAt:
      typeof raw.trialExpiresAt === 'string'
        ? raw.trialExpiresAt
        : typeof raw.trial_expires_at === 'string'
          ? raw.trial_expires_at
          : null,
    previousLoginAt:
      typeof raw.previousLoginAt === 'string'
        ? raw.previousLoginAt
        : typeof raw.previous_login_at === 'string'
          ? raw.previous_login_at
          : null,
  };
}

// 認証コンテキスト型定義
interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  heartbeatConnectionUnstable: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (username: string, email: string, password: string, hospitalName?: string) => Promise<any>;
  logout: () => void;
  checkSubscriptionStatus: () => Promise<void>;
  setAuthFromTokenResponse: (res: any) => Promise<boolean>;
  reloadUser: () => Promise<boolean>;
}

// 認証コンテキスト作成
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 認証プロバイダーコンポーネント
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [heartbeatConnectionUnstable, setHeartbeatConnectionUnstable] = useState(false);
  const heartbeatTransientFailureCountRef = useRef(0);

  const refreshSubscriptionDetail = useCallback(
    async (
      options?: { hasActiveSubscription?: boolean },
    ) => {
      const shouldFetch = options?.hasActiveSubscription ?? false;

      if (!shouldFetch) {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                subscriptionPeriodEnd: null,
                subscriptionCancelAtPeriodEnd: null,
                subscriptionDetailFetchedAt: Date.now(),
              }
            : prev,
        );
        return;
      }

      try {
        const detail = await fetchSubscriptionDetail();
        const nextPeriodEnd = detail.currentPeriodEnd ?? null;
        const nextCancellation = detail.cancelAtPeriodEnd ?? null;
        setUser((prev) =>
          prev
            ? {
                ...prev,
                subscriptionPeriodEnd: nextPeriodEnd,
                subscriptionCancelAtPeriodEnd: nextCancellation,
                subscriptionDetailFetchedAt: Date.now(),
              }
            : prev,
        );
      } catch (error) {
        console.error('サブスクリプション詳細取得エラー:', error);
      }
    },
    [],
  );

  // 初期認証チェック
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Cookieベースセッション検証APIを呼び出し
        const response = await axios.get(`${API_URL}/api/auth/me/`);

        // User型正規化
        const normalizedUser = normalizeUser(response.data);
        setToken(COOKIE_SESSION_TOKEN);
        setUser(normalizedUser);
        setIsAuthenticated(true);
        if (shouldRefreshSubscriptionForUser(normalizedUser)) {
          await refreshSubscriptionDetail({
            hasActiveSubscription: normalizedUser.hasActiveSubscription,
          });
        }

      } catch (error) {
        console.error('認証エラー:', error);
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    void checkAuth();
  }, [refreshSubscriptionDetail]);

  // ログイン関数
  const login = async (email: string, password: string): Promise<User> => {
    try {
      const normalizedEmail = normalizeEmail(email);
      const form = new URLSearchParams();
      form.append("username", normalizedEmail);
      form.append("password", password);

      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const { user: rawUser, verificationChallengeId } = response.data;
      const normalizedUser = normalizeUser(rawUser);
      if (typeof verificationChallengeId === 'string' && verificationChallengeId) {
        normalizedUser.verificationChallengeId = verificationChallengeId;
      }

      setToken(COOKIE_SESSION_TOKEN);
      setUser(normalizedUser);
      setIsAuthenticated(true);

      if (shouldRefreshSubscriptionForUser(normalizedUser)) {
        await refreshSubscriptionDetail({
          hasActiveSubscription: normalizedUser.hasActiveSubscription,
        });
      }

      return normalizedUser;
    } catch (error) {
      console.error("ログインエラー:", error);
      throw error;
    }
  };

  // ユーザー登録関数
  const register = async (username: string, email: string, password: string, hospitalName?: string): Promise<any> => {
    try {
      const normalizedEmail = normalizeEmail(email);
      const payload: any = { username, email: normalizedEmail, password };
      if (hospitalName) payload.hospitalName = hospitalName;
      const response = await axios.post(`${API_URL}/api/auth/register`, payload);
      return response.data;
    } catch (error) {
      console.error("登録エラー:", error);
      throw error;
    }
  };

  // ログアウト関数
  const logout = useCallback(() => {
    setToken(null);

    // 認証状態とユーザー情報をリセット
    setUser(null);
    setIsAuthenticated(false);

    void axios.post(`${API_URL}/api/auth/logout`).catch((error) => {
      console.warn('ログアウトAPI呼び出しに失敗しました', error);
    });
  }, []);

  // ログアウトハンドラをセッション期限切れハンドラに即時登録
  registerSessionExpirationLogout(logout);

  // サブスクリプション状態確認関数
  const checkSubscriptionStatus = async () => {
    if (!hasFullAuthenticatedSession(user) || user?.isViewer) {
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/subscription/status`);
      const hasActive = !!response.data.hasActiveSubscription;
      setUser((prev) => (prev ? { ...prev, hasActiveSubscription: hasActive } : prev));
      await refreshSubscriptionDetail({ hasActiveSubscription: hasActive });
    } catch (error) {
      console.error("サブスクリプション状態の確認エラー:", error);
    }
  };

  // レスポンスから認証情報をセットする汎用関数
  const setAuthFromTokenResponse = async (res: any): Promise<boolean> => {
    const { user: rawUser } = res;
    if (!rawUser) {
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }

    const normalizedUser = normalizeUser(rawUser);
    setToken(COOKIE_SESSION_TOKEN);
    setUser(normalizedUser);
    setIsAuthenticated(true);
    if (shouldRefreshSubscriptionForUser(normalizedUser)) {
      await refreshSubscriptionDetail({
        hasActiveSubscription: normalizedUser.hasActiveSubscription,
      });
    }

    return true;
  };

  // ユーザー情報をAPIから再取得し、ステートを更新する関数
  const reloadUser = async (): Promise<boolean> => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me/`);
      setToken(COOKIE_SESSION_TOKEN);
      const normalizedUser = normalizeUser(response.data);
      setUser(normalizedUser);
      setIsAuthenticated(true);
      if (shouldRefreshSubscriptionForUser(normalizedUser)) {
        await refreshSubscriptionDetail({
          hasActiveSubscription: normalizedUser.hasActiveSubscription,
        });
      }
      return true;
    } catch (error) {
      console.error('ユーザー再取得エラー:', error);
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  useEffect(() => {
    registerSessionExpirationLogout(logout);
    return () => registerSessionExpirationLogout(undefined);
  }, [logout]);

  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;

        if (shouldHandleSessionExpiration(status, detail)) {
          logout();
          redirectToLoginForSessionExpiration();
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [logout]);


  useEffect(() => {
    if (!isAuthenticated || !hasFullAuthenticatedSession(user)) {
      heartbeatTransientFailureCountRef.current = 0;
      setHeartbeatConnectionUnstable(false);
      return;
    }

    let stopped = false;
    const sendHeartbeat = async () => {
      try {
        await axios.post(`${API_URL}/api/auth/heartbeat`);
        if (stopped) {
          return;
        }
        heartbeatTransientFailureCountRef.current = 0;
        setHeartbeatConnectionUnstable(false);
      } catch (error: any) {
        if (stopped) {
          return;
        }

        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;

        if (isHeartbeatAuthenticationFailure(status)) {
          heartbeatTransientFailureCountRef.current = 0;
          setHeartbeatConnectionUnstable(false);
          logout();
          if (shouldHandleSessionExpiration(status, detail)) {
            redirectToLoginForSessionExpiration();
          }
          return;
        }

        heartbeatTransientFailureCountRef.current += 1;
        if (shouldShowHeartbeatConnectionWarning(heartbeatTransientFailureCountRef.current)) {
          setHeartbeatConnectionUnstable(true);
        }
        console.warn('ハートビート送信に一時的に失敗しました', error);
      }
    };

    void sendHeartbeat();
    const timer = window.setInterval(() => {
      void sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [isAuthenticated, user, logout]);

  // コンテキスト値の作成
  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAuthenticated,
        heartbeatConnectionUnstable,
        login,
        register,
        logout,
        checkSubscriptionStatus,
        setAuthFromTokenResponse,
        reloadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// カスタムフック
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
